<template>
  <div class="container">
    <div class="chat-container">
      <section>
        <div class="chat-content" @dblclick="openModalSetting">
          <div v-if="hasLimitHeight" :style="[styleChatContent, { '--logo-position': 'absolute' }]">
            <ContainerChatAutoScroll
              ref="refContainerScroll"
              :has-brick="true"
              :is-non-stop="isScrollNonStop"
              has-button-scroll
            >
              <div class="chat-message-container">
                <div class="chat-date">
                  <p class="small dim">{{ new Date().toDateString() }}</p>
                </div>

                <div v-if="!isPresentPartner && bottomBarStatus !== 2" class="alone-message">
                  <IconInfo class="icon-smaller icon-white icon-info" />
                  <p class="small white" v-html="t('screen_alone_message')"></p>
                </div>

                <template v-for="chatMessageScreen in messageList" :key="chatMessageScreen.callLogId">
                  <component
                    :is="getMessageComponent(chatMessageScreen)"
                    :message-list
                    :chat-message-screen
                    :bubble-call-log-id="chatMessageScreen.callLogId"
                  />
                </template>
              </div>
            </ContainerChatAutoScroll>

            <img src="/images/logo-sconai-full-timer-white.png" alt="logo" class="logo-sconai" />
          </div>

          <ContainerChatAutoScroll
            v-else
            ref="refContainerScroll"
            :has-button-scroll="true"
            :has-brick="true"
            :is-non-stop="isScrollNonStop"
          >
            <div class="chat-message-container">
              <div class="chat-date">
                <p class="small dim">{{ new Date().toDateString() }}</p>
              </div>
              <img src="/images/logo-sconai-full-timer-white.png" alt="logo" class="logo-sconai" />

              <div v-if="!isPresentPartner && bottomBarStatus !== 2" class="alone-message">
                <IconInfo class="icon-smaller icon-white icon-info" />
                <p class="small white" v-html="t('screen_alone_message')"></p>
              </div>

              <template v-for="chatMessageScreen in messageList" :key="chatMessageScreen.callLogId">
                <component
                  :is="getMessageComponent(chatMessageScreen)"
                  :message-list
                  :chat-message-screen
                  :bubble-call-log-id="chatMessageScreen.callLogId"
                />
              </template>
            </div>
          </ContainerChatAutoScroll>

          <ScreenModalSetting v-show="isVisibleModalSetting" @close="closeModalSetting" />
        </div>
      </section>

      <div v-if="isShinhanEvent" class="wrapper-logo">
        <img src="/images/logo-shinhan.png" alt="Shinhan Logo" class="image-logo" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, CSSProperties, inject, onMounted, onUnmounted, provide, Ref, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import ScreenMessageBubble from '@/components/domains/chat/chat-screen/ScreenMessageBubble.vue';
import ScreenMessageEnd from '@/components/domains/chat/chat-screen/ScreenMessageEnd.vue';
import ScreenModalSetting from '@/components/domains/chat/chat-screen/ScreenModalSetting.vue';
import IconInfo from '@/components/icons/IconInfo.vue';
import ContainerChatAutoScroll from '@/components/shared/composite/ContainerChatAutoScroll.vue';
import { useMessagesScreen } from '@/composables/domains/chat/useMessagesScreen';
import { useCallStore } from '@/stores/call';
import { SocketState, useSocketStore } from '@/stores/socket';
import { useUserStore } from '@/stores/user';
import { ContainerChatAutoScrollExposed } from '@/types/components';
import { Attendee, MappingProfile } from '@/types/socket';
import { ij_bottomBarStatus, ij_mappingProfile } from '@/utils/injectionKeys';
import { hashToIndex } from '@/utils/stringUtils';

const { t } = useI18n();
const socketStore = useSocketStore();
const callStore = useCallStore();
const userStore = useUserStore();
const { messageList, handleMessageWebSocket, resetScreenMessages } = useMessagesScreen();

const bottomBarStatus = inject(ij_bottomBarStatus) as Ref<number>;

const refContainerScroll = ref<ContainerChatAutoScrollExposed>();
const isScrollNonStop = ref(false);
const isVisibleModalSetting = ref(false);
let timeoutAutoScroll = 0;

// TODO: 신한 이벤트용 호스트 코드 여부
const isShinhanEvent = computed(() => ['rmlnm', 'rmliw', 'rmlib'].includes(callStore.qrCodeInfo.hostCode));

// 호스트가 있는지
const isPresentPartner = computed(() => {
  for (const attendee of socketStore.attendees) {
    if (attendee.isPartnerPerson === '1') return true;
  }
  return false;
});

// 참석자 아이디별 프로필 정보 (computed)
const mappingProfile = computed<MappingProfile>(() => {
  const primaryColors = ['navy', 'green', 'yellow', 'pink', 'purple'];
  const acc: MappingProfile = {};

  for (const attendee of Array.from(socketStore.attendees) as Attendee[]) {
    const { userId, nickname, ...rest } = attendee; // nickname is already decoded in setAttendees
    const nicknameDecoded = nickname ?? '';
    const initials = nicknameDecoded.charAt(0).toUpperCase();
    const color = primaryColors[hashToIndex(userId, primaryColors.length)];

    acc[userId] = {
      ...rest,
      color,
      initials,
      nickname: nicknameDecoded,
    };
  }

  return acc;
});

// 스크린 모드 고정된 높이 값이 있는지
const hasLimitHeight = computed(() => {
  return callStore.qrCodeInfo.options.screenHeight;
});

const styleChatContent = computed<CSSProperties>(() => {
  return hasLimitHeight.value
    ? {
        height: userStore.userPreferences['screenContainerHeight'] * 16 + 'px',
        position: 'relative',
        // border: '3px solid red',
      }
    : undefined;
});

const openModalSetting = () => {
  isVisibleModalSetting.value = true;
};

const closeModalSetting = () => {
  isVisibleModalSetting.value = false;
};

// 메세지 컴포넌트 결정(날짜, 입출, 더보기 등)
const getMessageComponent = (message) => {
  switch (message.flag) {
    case 'end':
      return ScreenMessageEnd;
    default:
      return ScreenMessageBubble;
  }
};

const handleParentMessage = (event: MessageEvent) => {
  const { type, code, ctrlKey } = event.data;

  if (type === 'RESET_EVENT' && ctrlKey && code === 'Backspace') {
    resetScreenMessages();

    if (refContainerScroll.value) {
      refContainerScroll.value.setMaxContentHeight(0);
    }
  }
};

const handleLocalKeydown = (event: KeyboardEvent) => {
  const isCtrl = event.ctrlKey || event.metaKey;

  if (isCtrl && event.code === 'Backspace') {
    event.preventDefault();

    window.parent.postMessage(
      {
        type: 'IFRAME_RESET_EVENT',
        code: event.code,
        ctrlKey: isCtrl,
      },
      '*'
    );
  }
};

onMounted(() => {
  window.addEventListener('message', handleParentMessage);
  window.addEventListener('keydown', handleLocalKeydown);
});

onUnmounted(() => {
  window.removeEventListener('message', handleParentMessage);
  window.removeEventListener('keydown', handleLocalKeydown);
});

// 소켓 연결 상태 감시
watch(
  () => socketStore.socketState,
  (state) => {
    if (SocketState.isOpen(state)) {
      socketStore.socket!.onmessage = (event) => {
        handleMessageWebSocket(event);
      };
    }
  },
  { immediate: true }
);

// 통화 종료 시 종료 메세지 추가
watch(bottomBarStatus, (newVal) => {
  if (newVal === 2) messageList.value.push({ callLogId: '0000000000', flag: 'end' });
});

// 텍스트 크기 변경 시 즉각 스크롤 후 원복
watch(
  () => userStore.userPreferences.textSizeMultiplier,
  () => {
    if (!refContainerScroll.value) return;

    isScrollNonStop.value = true;
    refContainerScroll.value.setIsScrollInstant(true);
    refContainerScroll.value.scrollToBottom();

    clearTimeout(timeoutAutoScroll);
    timeoutAutoScroll = setTimeout(() => {
      isScrollNonStop.value = false;
      refContainerScroll.value!.setIsScrollInstant(false);
    }, 2000);
  }
);

watch(
  () => messageList.value.length,
  () => {
    refContainerScroll.value?.setMaxContentHeight(0);
  }
);

provide(ij_mappingProfile, mappingProfile);
</script>

<style scoped lang="scss">
.container {
  width: 100%;
  height: 100dvh;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
}

.chat-container {
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex: 1;
}

.wrapper-logo {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 0;
}

.image-logo {
  width: 200px;
  height: auto;
  object-fit: contain;
}

.chat-date {
  display: flex;
  justify-content: center;
  margin-top: 0.75rem;
  margin-bottom: 0.75rem;
}

.logo-sconai {
  position: var(--logo-position, fixed);
  bottom: 0.5rem;
  right: 0.5rem;
  width: 5rem;
  padding: 0.25rem;
  background: var(--surface-glass-darker);
  border-radius: 4px;
  z-index: var(--z-floating);
}

.alone-message {
  position: relative;
  display: flex;
  width: fit-content;
  align-self: center;
  align-items: flex-start;
  max-width: 24rem;
  padding: 0.75rem 1rem;
  gap: 0.25rem;
  margin-bottom: 0.75rem;
  background-color: var(--surface-glass-dark);
  outline: 1px solid var(--surface-glass-lightdimmer);
  border-radius: 8px;
}

.chat-message {
  position: relative;
  display: flex;
  width: fit-content;
  align-self: center;
  max-width: 24rem;
  padding: 0.5rem 1rem;
  gap: 0.25rem;
  margin-bottom: 0.75rem;
  text-align: center;
  background-color: var(--surface-glass-dark);
  outline: 1px solid var(--surface-glass-lightdimmer);
  border-radius: 8px;
}

.icon-info {
  position: relative;
  top: 0.125rem;
  flex-shrink: 0;
}

.chat-content {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100dvh;
  background: var(--background-screen);
  overflow: hidden;
}

.chat-message-container {
  display: flex;
  width: 100%;
  flex-direction: column;
  flex-grow: 1;
  margin: auto;
}

.mic-input-container {
  position: absolute;
  bottom: 0;
  left: 0;
  margin-top: 0;
  width: 100%;
  display: flex;
  justify-content: center;
}

::v-deep .navy {
  border: solid 2px #a2adf6cc;
  background: none;
  color: #a2adf6cc;
}

::v-deep .green {
  border: solid 2px #87bbadcc;
  background: none;
  color: #87bbaddd;
}

::v-deep .yellow {
  border: solid 2px #c6c180cc;
  background: none;
  color: #c6c180dd;
}

::v-deep .pink {
  border: solid 2px #d5b0dccc;
  background: none;
  color: #d5b0dccc;
}

::v-deep .purple {
  border: solid 2px #a785cecc;
  background: none;
  color: #a785cecc;
}
</style>
